import { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import WhiteMainBackground from "../../../shared_components/WhiteMainBackground/WhiteMainBackground";
import MainHeading from "../../../shared_components/MainHeading/MainHeading";
import StyledButton from "../../../shared_components/StyledButton/StyledButton";
import NewQaAssetStyle from "./NewQaAsset.module.css";
import UserData from "../../../services/UserData";
import QaProcessService from "../../../services/QaProcessService";

const NewQaAsset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useContext(UserData);

  const referrer = location.state?.referrer || "/qa-process";

  const [assetName, setAssetName] = useState("");
  const [sourceBriefUrl, setSourceBriefUrl] = useState("");
  const [qaAssetType, setQaAssetType] = useState("plugin");
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const backButtonAction = () => {
    navigate(referrer);
  };

  const handleCancel = () => {
    navigate(referrer);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setValidationErrors({});

    const errors = {};
    if (!assetName.trim()) {
      errors.assetName = "Asset Name is required";
    }
    if (!sourceBriefUrl.trim()) {
      errors.sourceBriefUrl = "Source Brief URL (Teamwork link) is required";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    const trimmedUrl = sourceBriefUrl.trim();
    let existingAssets = [];
    try {
      existingAssets = await QaProcessService.getQaAssets(userData);
    } catch (err) {
      alert(`Unable to check existing assets: ${err.message || "Unknown error"}`);
      return;
    }

    const duplicateAsset = existingAssets.find(
      (asset) =>
        asset.sourceBriefUrl && asset.sourceBriefUrl.trim().toLowerCase() === trimmedUrl.toLowerCase()
    );

    if (duplicateAsset) {
      setValidationErrors({
        sourceBriefUrl: `An asset with this Teamwork link already exists: "${duplicateAsset.name}"`,
      });
      return;
    }

    setSubmitting(true);

    try {
      const created = await QaProcessService.createQaAsset(userData, {
        name: assetName.trim(),
        sourceBriefUrl: trimmedUrl,
        qaAssetType,
      });

      if (created?.assetKey) {
        QaProcessService.recordQaAssetLastAccessed(created.assetKey);
      }

      navigate("/qa-process/1");
    } catch (err) {
      alert(`Error creating QA asset: ${err.message}`);
      setSubmitting(false);
    }
  };

  return (
    <WhiteMainBackground>
      <div className={NewQaAssetStyle.main}>
        <MainHeading backButtonAction={backButtonAction}>
          CREATE NEW QA ASSET
        </MainHeading>

        <p className={NewQaAssetStyle.subtitle}>
          Fill in the details to generate a new QA record and checklist. Assignee and status are set by
          the server.
        </p>

        <form onSubmit={handleSubmit} className={NewQaAssetStyle.form}>
          <div className={NewQaAssetStyle.form_group}>
            <label htmlFor="assetName" className={NewQaAssetStyle.label}>
              Asset Name <span className={NewQaAssetStyle.required}>*</span>
              <span className={NewQaAssetStyle.label_hint}>(e.g., BUS_G10_Relationship_09-P)</span>
            </label>
            <input
              type="text"
              id="assetName"
              className={`${NewQaAssetStyle.input} ${validationErrors.assetName ? NewQaAssetStyle.input_error : ""}`}
              placeholder="Enter asset name..."
              value={assetName}
              onChange={(e) => {
                setAssetName(e.target.value);
                if (validationErrors.assetName) {
                  setValidationErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.assetName;
                    return newErrors;
                  });
                }
              }}
              required
            />
            {validationErrors.assetName && (
              <span className={NewQaAssetStyle.error_message}>
                {validationErrors.assetName}
              </span>
            )}
          </div>

          <div className={NewQaAssetStyle.form_group}>
            <label htmlFor="sourceBriefUrl" className={NewQaAssetStyle.label}>
              Source Brief URL (Teamwork Link) <span className={NewQaAssetStyle.required}>*</span>
            </label>
            <input
              type="url"
              id="sourceBriefUrl"
              className={`${NewQaAssetStyle.input} ${validationErrors.sourceBriefUrl ? NewQaAssetStyle.input_error : ""}`}
              placeholder="Enter Teamwork URL..."
              value={sourceBriefUrl}
              onChange={(e) => {
                setSourceBriefUrl(e.target.value);
                if (validationErrors.sourceBriefUrl) {
                  setValidationErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.sourceBriefUrl;
                    return newErrors;
                  });
                }
              }}
              required
            />
            {validationErrors.sourceBriefUrl && (
              <span className={NewQaAssetStyle.error_message}>
                {validationErrors.sourceBriefUrl}
              </span>
            )}
          </div>

          <div className={NewQaAssetStyle.form_group}>
            <label htmlFor="qaAssetType" className={NewQaAssetStyle.label}>
              Asset Type
            </label>
            <select
              id="qaAssetType"
              className={NewQaAssetStyle.select}
              value={qaAssetType}
              onChange={(e) => setQaAssetType(e.target.value)}
              required
            >
              <option value="plugin">Plugin</option>
              <option value="hardcoded">Hardcoded</option>
            </select>
          </div>

          <div className={NewQaAssetStyle.form_actions}>
            <StyledButton
              type="button"
              clickEvent={handleCancel}
              className={NewQaAssetStyle.cancel_button}
            >
              Cancel
            </StyledButton>
            <StyledButton
              type="submit"
              className={NewQaAssetStyle.create_button}
              disabled={submitting}
            >
              {submitting ? "Creating..." : "Create Asset"}
            </StyledButton>
          </div>
        </form>
      </div>
    </WhiteMainBackground>
  );
};

export default NewQaAsset;
